/** biome-ignore-all lint/suspicious/noExplicitAny: allow any */
import cx from 'classnames';
import {type FC, type ReactNode, useCallback, useRef, useState} from 'react';
import {useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import ReactSelect from 'react-select';
import {useIsLargeAndUp} from '~/common/hooks/useMatchQuery';
import {selectIsPremium} from '~/ViewInvitation/selectors/eventDetails';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {BottomSheet} from '~library/molecules/BottomSheet';
import {ChevronIcon} from '~library/svg/ChevronIcon';
import {EditLineIcon} from '~library/svg/EditLineIcon';
import {MoreIcon} from '~library/svg/MoreIcon';
import {PremiumIcon} from '~library/svg/PremiumIcon';
import {CardDesignIcon} from '~library/svg/v2/CardDesignIcon';
import {charcoal, premiumColor} from '~libSass/base/_exports.sass';

interface MoreOption {
  readonly show: boolean;
  readonly dataQaId: string;
  readonly label: ReactNode;
  readonly value: (() => void) | string;
}

interface StickyEditEventMoreOptionsProps {
  readonly eventId: string;
  readonly onRemoveAdsClick: () => void;
}

export const StickyEditEventMoreOptions: FC<StickyEditEventMoreOptionsProps> = ({
  eventId,
  onRemoveAdsClick,
}) => {
  const selectRef = useRef<any>(null);
  const ref = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const isLargeAndUp = useIsLargeAndUp();
  const isPremium = useSelector(selectIsPremium);
  const [showMenuMW, setShowMenuMW] = useState<boolean>(false);

  const toggleMenu = useCallback(() => {
    if (isLargeAndUp) {
      if (selectRef.current) {
        selectRef.current.focus();
        selectRef.current.openMenu();
      }
    } else {
      setShowMenuMW(true);
    }
  }, [isLargeAndUp]);

  const navigateTo = useCallback((value: (() => void) | string) => {
    if (typeof value === 'function') {
      value();
    } else {
      setShowMenuMW(false);
      window.location.href = value;
    }
  }, []);

  const handleSelectChange = useCallback(
    (selectedOption: MoreOption | null) => {
      if (selectedOption) {
        navigateTo(selectedOption.value);
      }
    },
    [navigateTo]
  );

  const moreOptionsList: MoreOption[] = [
    {
      show: !isPremium && !isLargeAndUp,
      dataQaId: 'option-remove-ads',
      label: (
        <Typography className="sticky-ctas__select-option" variant="paragraph2">
          <PremiumIcon ratio={0.125} color={premiumColor} />
          Remove ads
        </Typography>
      ),
      value: () => onRemoveAdsClick(),
    },
    {
      show: true,
      dataQaId: 'option-edit-invitation',
      label: (
        <Typography className="sticky-ctas__select-option" variant="paragraph2">
          <EditLineIcon ratio={0.75} />
          Edit Invitation
        </Typography>
      ),
      value: `/invitation/${eventId}/customize`,
    },
    {
      show: true,
      dataQaId: 'option-see-guest-list',
      label: (
        <Typography
          className="sticky-ctas__select-option sticky-ctas__select-option--last"
          variant="paragraph2"
        >
          <CardDesignIcon ratio={0.75} />
          See guest list
        </Typography>
      ),
      value: () => {
        setShowMenuMW(false);
        history.push({
          pathname: '/guests',
          search: history.location.search,
        });
      },
    },
  ];

  const moreOptions = moreOptionsList.filter((option) => option.show);

  const styles = {
    control: (base: any) => ({
      ...base,
      position: 'absolute',
      left: '-9999px',
      pointerEvents: 'none',
    }),
    menu: (base: any) => ({
      ...base,
      width: '11.25rem',
    }),
  };

  return (
    <div
      className={cx('sticky-ctas__more-options', {
        'sticky-ctas__more-options-premium': isPremium,
      })}
      ref={ref}
    >
      <ReactSelect
        ref={selectRef}
        className="sticky-ctas__more-options-select"
        options={moreOptions}
        placeholder="More options"
        value={null}
        menuPlacement="top"
        onChange={handleSelectChange}
        data-qa-id="more-options-select"
        styles={styles}
      />
      <BottomSheet
        in={showMenuMW}
        className="sticky-ctas__bottom-sheet"
        title="More Options"
        onClose={() => setShowMenuMW(false)}
        content={moreOptions.map((option) => (
          <Button
            key={option.dataQaId}
            variant="unstyled-button"
            onClick={() => handleSelectChange(option)}
            data-qa-id={option.dataQaId}
          >
            {option.label}
          </Button>
        ))}
      />
      <Button
        className="sticky-ctas_more-options-trigger"
        variant="secondary"
        size="large"
        data-qa-id="more-options-trigger"
        onClick={toggleMenu}
        fullWidth={!isLargeAndUp}
      >
        {isPremium || !isLargeAndUp ? (
          <>
            More options
            {isLargeAndUp && <ChevronIcon direction="down" ratio={0.75} />}
          </>
        ) : (
          <MoreIcon color={charcoal} />
        )}
      </Button>
    </div>
  );
};
